import React from 'react';

import { Button, Group, Paper, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import KiloGLMActionSelect from './KiloGLMActionSelect';
import KiloGLMAppointmentTypeSelect from './KiloGLMAppointmentTypeSelect';
import KiloGLMAthleteStatusSelect from './KiloGLMAthleteStatusSelect';
import KiloGLMCheckinsNumber from './KiloGLMCheckinsNumber';
import KiloGLMClassTypeSelect from './KiloGLMClassTypeSelect';
import KiloGLMEnableSwitch from './KiloGLMEnableSwitch';
import KiloGLMPackageSelect from './KiloGLMPackageSelect';
import KiloGLMPipelineStageSelect from './KiloGLMPipelineStageSelect';
import KiloGLMTagSelect from './KiloGLMTagSelect';
import KiloGLMTriggerSelect from './KiloGLMTriggerSelect';
import { FormValues, GLMCardTypes, KiloGLMFlowActions, KiloGLMFlowTriggers } from '../types';

// Triggers whose action is auto-set and needs no config: trigger → enable.
const TWO_STEP_TRIGGERS = new Set<string>([
  KiloGLMFlowTriggers.ATHLETE_DATA_UPDATED,
  KiloGLMFlowTriggers.NOTE_CREATED_OR_EDITED,
  KiloGLMFlowTriggers.NOTE_DELETED,
]);

const StepIndicator = ({
  currentStep,
  totalSteps,
}: {
  currentStep: number;
  totalSteps: number;
}): JSX.Element => (
  <Group spacing={0} align='center' style={{ width: '100%' }}>
    {Array.from({ length: totalSteps }, (_, i) => {
      const stepNum = i + 1;
      const isActive = stepNum <= currentStep;
      return (
        <React.Fragment key={stepNum}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: isActive ? '#16B8FF' : '#dee2e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isActive ? '#fff' : '#868e96',
              fontWeight: 600,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {stepNum}
          </div>
          {stepNum < totalSteps && (
            <div
              style={{
                flex: 1,
                height: 2,
                backgroundColor: stepNum < currentStep ? '#16B8FF' : '#dee2e6',
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </Group>
);

interface IGLMFlowBuilderSidebarProps {
  handleTriggerSelect: (value: string) => void;
  handleActionSelect: (value: string) => void;
  handleTagSelect: (tags: string[]) => void;
  onCardSelect: (cardType: GLMCardTypes) => void;
  onBack: () => void;
  onSave: () => void;
  canGoBack: boolean;
  selectedCard?: GLMCardTypes;
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
  type: 'create' | 'edit';
  stepNumber: number;
}

const GLMFlowBuilderSidebar = ({
  handleTriggerSelect,
  handleActionSelect,
  handleTagSelect,
  onCardSelect,
  onBack,
  onSave,
  canGoBack,
  selectedCard,
  form,
  type,
  stepNumber,
}: IGLMFlowBuilderSidebarProps): JSX.Element => {
  const handleBack = (): void => {
    switch (selectedCard) {
      case 'trigger':
        form.setFieldValue('trigger', '');
        break;
      case 'package':
        form.setFieldValue('packageIds', []);
        break;
      case 'status':
        form.setFieldValue('athleteStatus', '');
        break;
      case 'num_checkins':
        form.setFieldValue('checkinsNumber', null);
        break;
      case 'appointment_type':
        form.setFieldValue('appointmentTypeIds', []);
        break;
      case 'class_type':
        form.setFieldValue('classTypeIds', []);
        break;
      case 'action':
        form.setFieldValue('action', '');
        break;
      case 'tag':
        form.setFieldValue('tags', []);
        break;
      case 'pipeline_stage':
        form.setFieldValue('acPipelineId', null);
        form.setFieldValue('acStageId', null);
        break;
    }
    onBack();
  };

  const totalSteps = TWO_STEP_TRIGGERS.has(form.values.trigger) ? 2 : 5;
  const getNextCard = (): GLMCardTypes => {
    switch (selectedCard) {
      case 'trigger': {
        const { trigger } = form.values;
        if (!trigger) return 'trigger';
        if (trigger === KiloGLMFlowTriggers.ATHLETE_CREATED) return 'action';
        if (trigger.includes('Package')) return 'package';
        if (trigger === KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED) return 'status';
        if (trigger === KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED) return 'num_checkins';
        if (trigger === KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN) return 'appointment_type';
        if (trigger === KiloGLMFlowTriggers.CLASS_CHECKIN) return 'class_type';
        return 'enabled';
      }
      case 'package':
      case 'status':
      case 'num_checkins':
      case 'appointment_type':
      case 'class_type':
        return 'action';
      case 'action': {
        const { action } = form.values;
        if (
          action === KiloGLMFlowActions.ADD_TAGS ||
          action === KiloGLMFlowActions.REMOVE_TAGS ||
          action === KiloGLMFlowActions.LINK_CONTACT_AND_ADD_TAG
        )
          return 'tag';
        if (action === KiloGLMFlowActions.LINK_CONTACT_AND_MOVE_PIPELINE_STAGE) return 'pipeline_stage';
        return 'enabled';
      }
      case 'tag':
      case 'pipeline_stage':
        return 'enabled';
      default:
        return null;
    }
  };

  const isContinueDisabled = (): boolean => {
    switch (selectedCard) {
      case 'trigger':
        return !form.values.trigger;
      case 'package':
        return !form.values.packageIds?.length;
      case 'status':
        return !form.values.athleteStatus;
      case 'num_checkins': {
        const n = form.values.checkinsNumber;
        return !(typeof n === 'number' && Number.isInteger(n) && n > 0);
      }
      case 'appointment_type':
        return !form.values.appointmentTypeIds?.length;
      case 'class_type':
        return !form.values.classTypeIds?.length;
      case 'action':
        return !form.values.action;
      case 'tag':
        return !form.values.tags?.length;
      case 'pipeline_stage':
        return !form.values.acPipelineId || !form.values.acStageId;
      default:
        return true;
    }
  };

  const nextCard = getNextCard();

  return (
    <Paper shadow='xl' radius='md' withBorder py='sm' px={0} style={{ minWidth: '300px', height: '100%' }}>
      <Stack
        justify='space-between'
        style={{
          height: '100%',
          paddingTop: '20px',
          paddingLeft: '30px',
          paddingRight: '30px',
          paddingBottom: '20px',
        }}
      >
        <StepIndicator currentStep={stepNumber} totalSteps={totalSteps} />
        <Stack align='stretch' style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {selectedCard === 'trigger' && (
            <KiloGLMTriggerSelect handleTriggerSelect={handleTriggerSelect} form={form} />
          )}
          {selectedCard === 'package' && <KiloGLMPackageSelect form={form} type={type} />}
          {selectedCard === 'status' && <KiloGLMAthleteStatusSelect form={form} />}
          {selectedCard === 'action' && (
            <KiloGLMActionSelect handleActionSelect={handleActionSelect} form={form} />
          )}
          {selectedCard === 'num_checkins' && <KiloGLMCheckinsNumber form={form} />}
          {selectedCard === 'appointment_type' && <KiloGLMAppointmentTypeSelect form={form} />}
          {selectedCard === 'class_type' && <KiloGLMClassTypeSelect form={form} />}
          {selectedCard === 'pipeline_stage' && <KiloGLMPipelineStageSelect form={form} />}
          {selectedCard === 'tag' && <KiloGLMTagSelect handleTagSelect={handleTagSelect} form={form} />}
          {selectedCard === 'enabled' && <KiloGLMEnableSwitch form={form} />}
        </Stack>
        <Group grow>
          <Button type='button' variant='default' onClick={handleBack} disabled={!canGoBack}>
            Back
          </Button>
          {selectedCard === 'enabled' ? (
            <Button type='button' onClick={onSave}>
              Save automation
            </Button>
          ) : (
            nextCard !== null && (
              <Button type='button' onClick={() => onCardSelect(nextCard)} disabled={isContinueDisabled()}>
                Continue
              </Button>
            )
          )}
        </Group>
      </Stack>
    </Paper>
  );
};

export default GLMFlowBuilderSidebar;
